import { Navigate, Outlet, useLocation } from 'react-router'

import type { EstadoDeRedirecionamento } from '@/auth/destino'
import { useSession } from '@/auth/session'
import { SemPerfil } from '@/auth/SemPerfil'
import { TelaDeEspera } from '@/auth/TelaDeEspera'

/**
 * Route guard — story 0.3 AC-3.
 *
 * A pathless layout route above the whole shell, so every screen inherits it and no
 * new route can forget to opt in. Adding a route inside this branch protects it;
 * that is the point.
 *
 * The destination goes in the router's **location state**, never in a query string:
 * `?de=/cliente/abc/...` is copied into chat messages, written to access logs and
 * kept in browser history, and the tenant id is exactly the sort of thing that
 * should not end up there. State is in-memory and travels with the navigation.
 *
 * ⚠️ Client-side gating is UX, not the security boundary (BR-17). This keeps a signed
 * out user from staring at an empty screen; the server refusing the call is the
 * control.
 */
export function RotaProtegida() {
  const { sessao, carregando, erroDePerfil, saidaDeliberada } = useSession()
  const location = useLocation()

  // Still asking. Redirecting now would bounce a signed-in user to `/login` on every
  // reload and lose the destination they typed.
  if (carregando) return <TelaDeEspera />

  if (!sessao) {
    /**
     * 🔒 Depois de um logout DELIBERADO, o caminho atual não é destino de
     * ninguém — ele é o caminho do usuário ANTERIOR, e carrega o `idCliente`
     * dele.
     *
     * Sem esta distinção: o Arquiteto sai de
     * `/cliente/tribunal-de-justica/finops/oportunidades`, o Cliente entra no
     * mesmo tab, é levado para lá e recebe "você não tem acesso a este
     * cliente" — uma mensagem que ainda por cima NOMEIA um tenant ao qual ele
     * não tem acesso. Relatado pelo Fábio em 2026-08-21.
     *
     * Uma sessão EXPIRADA continua guardando o destino: ali é o mesmo usuário
     * voltando, e devolvê-lo à tela onde estava é o ponto do AC-3.
     */
    const estado: EstadoDeRedirecionamento = saidaDeliberada
      ? {}
      : { de: `${location.pathname}${location.search}` }
    return <Navigate to="/login" replace state={estado} />
  }

  // Autenticado, mas sem permissões. Não é motivo para mandar de volta ao `/login` —
  // o login funcionou. A tela nomeia o que faltou e oferece as duas saídas reais.
  if (erroDePerfil) return <SemPerfil />

  return <Outlet />
}
